/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useState, useEffect, useCallback } from "react";
import { getDatabase, ref, set, update, onValue } from "firebase/database";
import * as Sentry from "@sentry/react";
import { useAuth } from "@/contexts/AuthContext";
import { sendOneOffSms } from "@/utils/walkMeHome/smsClient";
import { senderLabel } from "@/utils/safecircle/notificationPayload";
import { buildContactInvitationSms } from "@/utils/safecircle/contactConfirmationSms";
import { normalizePhone } from "@/lib/phone";
import {
  buildPendingIndexWrite,
  buildPendingIndexClear,
} from "@/utils/safecircle/pendingContactIndex";

/**
 * Two-step opt-in status. A newly-added contact is `pending` until they reply
 * YES to the invitation SMS (→ `confirmed`) or STOP (→ `declined`).
 *
 * GRANDFATHER (fail-safe): a contact with NO status field is a legacy record —
 * treated as eligible (confirmed) AT THE GATE (see eligibleForSms, PR 3), NOT
 * migrated. Only explicit `pending`/`declined` are excluded from SOS/WMH. So an
 * unmigrated legacy contact always still gets alerted. NEW adds always write an
 * explicit `status: "pending"`.
 */
export type ContactStatus = "pending" | "confirmed" | "declined";

export interface EmergencyContact {
  id: string;
  name: string;
  phone: string;
  /** Relationship label the user picks ("Mom", "Best friend", custom free
   *  text, …). Added for the phone-first onboarding circle-of-trust step;
   *  older records simply omit it, so it is optional. */
  relation?: string;
  /** Two-step opt-in status. ABSENT on legacy records = grandfathered (see
   *  above). New adds are always "pending". */
  status?: ContactStatus;
  /** When the invitation SMS was sent (new adds). */
  invitedAt?: number;
  /** When the recipient replied YES (set by api/sms-inbound in PR 3). */
  confirmedAt?: number;
}

const MAX_CONTACTS = 3;

export function useEmergencyContacts() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [loading, setLoading] = useState(true);

  // Listen to Firebase RTDB
  useEffect(() => {
    if (!user) {
      setContacts([]);
      setLoading(false);
      return;
    }
    const db = getDatabase();
    const contactsRef = ref(db, `users/${user.uid}/emergencyContacts`);
    const unsub = onValue(contactsRef, (snapshot) => {
      const data = snapshot.val();
      if (data && Array.isArray(data)) {
        setContacts(data.filter(Boolean));
      } else {
        setContacts([]);
      }
      setLoading(false);
    });
    return () => unsub();
  }, [user]);

  const saveToFirebase = useCallback(
    (updated: EmergencyContact[]) => {
      if (!user) return;
      const db = getDatabase();
      set(ref(db, `users/${user.uid}/emergencyContacts`), updated);
    },
    [user]
  );

  const addContact = useCallback(
    (name: string, phone: string, relation?: string) => {
      if (contacts.length >= MAX_CONTACTS) return false;
      if (!user) return false;
      const trimmedRelation = relation?.trim();
      const now = Date.now();
      // Callers (EmergencyContacts.tsx, EmergencyContactsScreen.tsx) are
      // expected to already validate + normalize to E.164 before calling —
      // this is a defense-in-depth re-check so the stored record is NEVER a
      // raw, un-normalized string that could mis-dial on send (the "+1" by
      // hand / missing-country-code bug). Falls back to the trimmed raw
      // input only when it somehow fails to parse, so a caller's own
      // validated-but-not-yet-normalized value is never silently dropped.
      const trimmedPhone = phone.trim();
      const normalized = normalizePhone(trimmedPhone, "US");
      const storedPhone =
        "e164" in normalized ? normalized.e164 : trimmedPhone;
      // Two-step opt-in: new contacts start "pending" and receive nothing until
      // they reply YES. `invitedAt` records when the invitation SMS went out.
      const entry: EmergencyContact = {
        id: `ec_${now}`,
        name: name.trim(),
        phone: storedPhone,
        status: "pending",
        invitedAt: now,
      };
      // Only persist `relation` when set — keeps the stored shape unchanged for
      // callers that don't pass one (e.g. the legacy EmergencyContacts editor).
      if (trimmedRelation && trimmedRelation.length > 0) {
        entry.relation = trimmedRelation;
      }
      const updated = [...contacts, entry];
      setContacts(updated);

      // Persist the contact itself (owner-only rule already permits this — it
      // can NEVER be blocked by a missing pendingContactIndex rule).
      saveToFirebase(updated);

      // SEPARATELY + best-effort: write the pending-contact reverse-index entry
      // (phone → {uid, contactId}) so an inbound YES from this number finds this
      // add. Deliberately NOT part of the contact write and .catch-swallowed, so
      // a missing/denied index rule (until database.rules.json ships in PR 2's
      // deploy) can't block or roll back the add. Index key is canonical E.164;
      // if the phone can't normalize we skip it (a YES won't auto-match).
      const db = getDatabase();
      if ("e164" in normalized) {
        const e164 = normalized.e164;
        update(
          ref(db),
          buildPendingIndexWrite(e164, user.uid, {
            contactId: entry.id,
            invitedAt: now,
          }),
        ).catch((err) => {
          Sentry.addBreadcrumb({
            category: "emergency-contacts",
            level: "warning",
            message: "pending_index_write_failed",
            data: { detail: String(err) },
          });
        });
      }

      // Two-step INVITATION SMS: ask the recipient to reply YES to opt in.
      // FIRE-AND-FORGET — not awaited, sendOneOffSms never throws, so an SMS
      // failure (A2P not yet approved, offline) can never block or roll back the
      // add. ADD only — updateContact never sends.
      void sendOneOffSms({
        to: entry.phone,
        body: buildContactInvitationSms(
          senderLabel(user?.displayName, user?.email),
        ),
        getIdToken: () => user?.getIdToken() ?? Promise.resolve(null),
      })
        .then((outcome) => {
          if (!outcome.ok) {
            // info/warning, NOT error — every add while A2P is pending will
            // fail to send; don't spam Sentry alerts with it.
            Sentry.addBreadcrumb({
              category: "emergency-contacts",
              level: "warning",
              message: "contact_invitation_sms_failed",
              data: { detail: outcome.errorDetail ?? "send failed" },
            });
          }
        })
        .catch(() => {
          // Defensive: sendOneOffSms is designed never to reject, but swallow
          // anything anyway so the add can never be affected.
        });

      return true;
    },
    [contacts, user]
  );

  const updateContact = useCallback(
    (id: string, name: string, phone: string) => {
      const trimmedPhone = phone.trim();
      const normalized = normalizePhone(trimmedPhone, "US");
      const storedPhone =
        "e164" in normalized ? normalized.e164 : trimmedPhone;
      const updated = contacts.map((c) =>
        c.id === id ? { ...c, name: name.trim(), phone: storedPhone } : c
      );
      setContacts(updated);
      saveToFirebase(updated);
    },
    [contacts, saveToFirebase]
  );

  const removeContact = useCallback(
    (id: string) => {
      if (!user) return;
      const removed = contacts.find((c) => c.id === id);
      const updated = contacts.filter((c) => c.id !== id);
      setContacts(updated);

      // Persist the removal (owner-only rule).
      saveToFirebase(updated);

      // SEPARATELY + best-effort: clear THIS user's pending-index entry for the
      // removed number so a later YES can't match a contact that no longer
      // exists. Other users who added the same number keep their entries.
      const db = getDatabase();
      const removedNormalized = removed
        ? normalizePhone(removed.phone, "US")
        : null;
      const e164 =
        removedNormalized && "e164" in removedNormalized
          ? removedNormalized.e164
          : null;
      if (e164) {
        update(ref(db), buildPendingIndexClear(e164, user.uid)).catch((err) => {
          Sentry.addBreadcrumb({
            category: "emergency-contacts",
            level: "warning",
            message: "pending_index_clear_failed",
            data: { detail: String(err) },
          });
        });
      }
    },
    [contacts, saveToFirebase, user]
  );

  return { contacts, loading, addContact, updateContact, removeContact, canAdd: contacts.length < MAX_CONTACTS };
}
